<template>
  <panel title="Płatności">
    <w-table
      entity-type="payment"
      :hide-add-button="true"
      :hide-search-input="true"
      :actions="actions"
      :columns="columns"
      :data="list"
      :total="count"
      :per-page="-1"
      @cancel="cancel"
      @cancelKsat="cancelInKsat"
      @reattach="reattach"
      @view="preview"
    />

    <div slot="footer">
      <check-permission v-if="pack.structure" permissions="PAYMENT.MDOK" entity-type="paymentPack" :entity-id="packId">
        <div>
          <post-form
            class="float-right ml-2"
            button-class="btn-primary"
            text="Podgląd PDF"
            aria-label-text="Podgląd PDF"
            :url="downloadPdfLink"
          />

          <w-button class="btn-primary float-right ml-2" text="Prześlij do MDOK" @click="sendToMdok" />

          <w-button v-if="mdokUid" icon="fas fa-link mr-2" class="btn-light" text="Przejdź do MDOK" @click="goToMdok" />
        </div>
      </check-permission>
    </div>

    <payment-details-modal :show="modals.preview" @close="closeModal('preview')" @resend="resend" />
  </panel>
</template>

<script>
import { mapState } from 'vuex';

import CheckPermission from '@/components/helpers/CheckPermission';
import PaymentDetailsModal from '@/components/modals/PaymentDetailsModal';
import Panel from '@/components/panel/Panel';
import PostForm from '@/components/postForm/PostForm';
import WTable from '@/components/table/Table';
import { Errors } from '@/enums/errors.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { date, dateTime } from '@/filters/date';
import { displayModalMixin } from '@/mixins/displayModal';
import { listMixin } from '@/mixins/list';
import { documentResource } from '@/resources/document.resource';
import { paymentPackResource } from '@/resources/paymentPack.resource';
import { config } from '@/utils/config';
import { CANCEL_ACTION, CANCEL_KSAT_ACTION, REATTACH_ACTION, VIEW_ACTION } from '@/utils/constants';
import { tryParseInt } from '@/utils/number';
import { getPaymentPack } from '@/utils/paymentList';
import { serverError } from '@/utils/serverError';
import { Successes } from '@/enums/successes.enum';

const messages = {};

// @vuese
// Lista płatności
// @group Procedure
export default {
  components: { Panel, PaymentDetailsModal, PostForm, WTable },

  mixins: [displayModalMixin, listMixin('payment', messages)],

  data() {
    return {
      actions: [VIEW_ACTION, CANCEL_ACTION, CANCEL_KSAT_ACTION, REATTACH_ACTION]
    };
  },

  computed: {
    ...mapState({
      splitDisabled: state => state.setting.settings[PublicSettings.PAYMENT_NO_SPLIT]?.value === 'true',
      pack: state => state.paymentPack.list[0],
      isSecretary: state => !!state.auth.nodes?.length
    }),

    columns() {
      const columns = [
        { name: 'id', displayName: 'ID', sortable: true },
        { name: 'number', displayName: 'Numer', sortable: true },
        { name: 'created', displayName: 'Data utworzenia', sortable: true, nameTransformer: value => dateTime(value) },
        { name: 'date', displayName: 'Termin płatności', sortable: true, nameTransformer: value => date(value) },
        { name: 'data.contractor.fullName', displayName: 'Imię i nazwisko' },
        { name: 'data.contractor.bankAccountNumber', displayName: 'Numer konta' },
        { name: 'pack.structure.name', displayName: 'Jednostka', nameTransformer: value => value || 'OW' },
        { name: 'externalId', displayName: 'Identyfikator KSAT', sortable: true },
        { name: 'amount', displayName: 'Kwota', sortable: true }
      ];
      if (!this.splitDisabled) {
        columns.push({ name: 'grant', displayName: 'Dotacja' }, { name: 'contribution', displayName: 'Wkład własny' });
      }
      return columns;
    },

    packId() {
      return this.$route.params.packId;
    },

    downloadPdfLink() {
      return `${config.renderURL}/pdf/payment_list/${this.packId}`;
    },

    mdokUid() {
      if (this.isSecretary) {
        return this.pack.systemNumbers?.mdokJoId;
      }
      return this.pack.systemNumbers?.mdokId;
    }
  },
  methods: {
    // @vuese
    // Przesyła paczkę do MDOK
    sendToMdok() {
      paymentPackResource
        .sendToMdok(tryParseInt(this.packId))
        .then(() => this.notifySuccess('Paczka została przesłana do MDOK.'))
        .then(() => getPaymentPack(tryParseInt(this.packId)))
        .catch(err => this.notifyError(serverError(err)));
    },

    // @vuese
    // Przechodzi do paczki w MDOK
    goToMdok() {
      documentResource
        .getMdokUrlByUid(tryParseInt(this.mdokUid), undefined, undefined, tryParseInt(this.packId))
        .then(url => window.open(url, '_blank'));
    },

    // @vuese
    // Anulowanie wypłaty
    cancel(payment) {
      this.$store.dispatch('setConfirm', {
        title: 'Potwierdź anulowanie zobowiązania',
        description: `Czy jesteś pewien, że chcesz anulować zobowiązanie numer "${payment.number}" tylko w systemie Witkac?`,
        onSuccess: () =>
          this.$store
            .dispatch('payment/cancel', payment.id)
            .then(() => {
              this.notifySuccess(Successes.PAYMENT_CANCELED);
              this.updateList(this.$route);
            })
            .catch(() => this.notifyError(Errors.PAYMENT_CANNOT_CANCEL))
      });
    },

    // @vuese
    // Anulowanie wypłaty w KSAT
    cancelInKsat(payment) {
      this.$store.dispatch('setConfirm', {
        title: 'Potwierdź anulowanie zobowiązania',
        description: `Czy jesteś pewien, że chcesz anulować zobowiązanie numer "${payment.number}" w systemach Witkac i KSAT?`,
        onSuccess: () =>
          this.$store
            .dispatch('payment/cancelInKsat', payment.id)
            .then(() => {
              this.notifySuccess(Successes.PAYMENT_CANCELED);
              this.updateList(this.$route);
            })
            .catch(() => this.notifyError(Errors.PAYMENT_CANNOT_CANCEL))
      });
    },

    // @vuese
    // Dołączenie zobowiązania do paczki dokumentów
    reattach(payment) {
      this.$store.dispatch('setConfirm', {
        title: 'Potwierdź dołączenie zobowiązania do paczki',
        description: `Czy jesteś pewien, że chcesz dołączyć zobowiązanie numer "${payment.number}" do paczki dokumentów w systemie KSAT?`,
        onSuccess: () =>
          this.$store
            .dispatch('payment/reattachInKsat', payment.id)
            .then(() => {
              this.notifySuccess(Successes.PAYMENT_REATTACHED);
              this.updateList(this.$route);
            })
            .catch(() => this.notifyError(Errors.PAYMENT_CANNOT_REATTACH))
      });
    },

    // @vuese
    // Ponowne przesłanie zobowiązania do KSAT
    resend(variables) {
      this.$store
        .dispatch('payment/resendToKsat', variables)
        .then(() => {
          this.notifySuccess(Successes.PAYMENT_RESENT);
          this.closeModal('preview');
          this.updateList(this.$route);
        })
        .catch(() => this.notifyError(Errors.PAYMENT_CANNOT_RESEND));
    },

    // @vuese
    // Otwiera modal podglądu
    preview(payment) {
      this.$store
        .dispatch('documentIndex/get', payment.documentId)
        .then(() => {
          this.$store.commit('payment/setEntity', payment);
          this.openModal('preview');
        })
        .catch(err => this.notifyError(Errors.DOCUMENT_CANNOT_GET));
    },

    // @vuese
    // Filtry listy
    getFilters(route) {
      return {
        count: -1,
        start: 0,
        packId: tryParseInt(this.packId),
        structureIds: this.$store.state.auth.nodes.map(node => node.id)
      };
    }
  }
};
</script>
